import { useState, useCallback, useEffect, useRef } from 'react'
import { api, ApiError, getDeviceId } from '../utils/api'
import logger from '../utils/logger'
import { toast } from 'react-hot-toast'
import { trackCloneModeToggled, trackClonePollCreated, trackErrorClonePoll } from '../utils/analytics'

/**
 * Custom hook for managing clone poll functionality
 * @param {Array} choices - Current choices array
 * @param {Function} navigate - React Router navigate function
 * @returns {Object} Hook state and functions
 */
function useClonePoll(choices, navigate) {
  const [cloneMode, setCloneMode] = useState(false)
  const [selectedChoices, setSelectedChoices] = useState(new Set())
  const [showCloneModal, setShowCloneModal] = useState(false)
  const [clonePollName, setClonePollName] = useState('')
  const [isCloning, setIsCloning] = useState(false)
  const isFirstRender = useRef(true)

  // Show toast when clone mode changes (after initial render)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    if (cloneMode) {
      toast('Select choices to create new poll', { icon: '📋' })
    }
  }, [cloneMode])

  const toggleCloneMode = useCallback((pollId) => {
    setCloneMode(prev => {
      const newMode = !prev
      trackCloneModeToggled(pollId, newMode)
      if (!newMode) {
        // Clear selections when turning off clone mode
        setSelectedChoices(new Set())
      }
      return newMode
    })
  }, [])

  const toggleChoiceSelection = useCallback((choiceId) => {
    setSelectedChoices(prev => {
      const newSelected = new Set(prev)
      if (newSelected.has(choiceId)) {
        newSelected.delete(choiceId)
      } else {
        newSelected.add(choiceId)
      }
      return newSelected
    })
  }, [])

  const handleClonePoll = useCallback(async () => {
    const pollName = clonePollName.trim()
    if (!pollName) {
      toast.error('Please enter a poll name')
      return
    }

    if (pollName.length > 150) {
      toast.error('Poll name must be 150 characters or less')
      return
    }

    if (selectedChoices.size === 0) {
      toast.error('Please select at least one choice to clone')
      return
    }

    setIsCloning(true)

    try {
      logger.info('Starting clone poll process', {
        selectedChoiceCount: selectedChoices.size,
        pollName: pollName.substring(0, 50) + '...'
      })

      // Get selected choice texts
      const selectedChoiceTexts = choices
        .filter(choice => selectedChoices.has(choice.id))
        .map(choice => choice.text)

      // Create new poll with choices - include device_id for ownership
      const newPollResponse = await api.post('/api/polls', {
        title: pollName,
        choices: selectedChoiceTexts.map(text => ({ text })),
        device_id: getDeviceId()
      })
      const newPollId = newPollResponse.id
      const newPollShortCode = newPollResponse.short_code

      logger.info('New poll created successfully from clone', {
        newPollId,
        newPollShortCode,
        choiceCount: selectedChoiceTexts.length
      })

      toast.success(`Successfully created poll with ${selectedChoiceTexts.length} choice${selectedChoiceTexts.length !== 1 ? 's' : ''}!`)
      trackClonePollCreated(newPollId, selectedChoiceTexts.length)

      // Close modal and reset state
      setShowCloneModal(false)
      setClonePollName('')
      setSelectedChoices(new Set())
      setCloneMode(false)

      // Redirect to new poll
      navigate(`/poll/${newPollShortCode}`)

    } catch (error) {
      logger.error('Clone poll failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        selectedChoiceCount: selectedChoices.size,
        pollName
      })

      if (error instanceof ApiError) {
        toast.error(error.message || 'Failed to create poll. Please try again.')
        trackErrorClonePoll(null, error.message)
      } else {
        toast.error('Failed to create poll. Please try again.')
        trackErrorClonePoll(null, 'Unknown error')
      }
    } finally {
      setIsCloning(false)
    }
  }, [choices, selectedChoices, clonePollName, navigate])

  const openCloneModal = useCallback(() => {
    setShowCloneModal(true)
  }, [])

  const closeCloneModal = useCallback(() => {
    setShowCloneModal(false)
    setClonePollName('')
  }, [])

  return {
    cloneMode,
    selectedChoices,
    showCloneModal,
    clonePollName,
    isCloning,
    setClonePollName,
    toggleCloneMode,
    toggleChoiceSelection,
    handleClonePoll,
    openCloneModal,
    closeCloneModal
  }
}

export default useClonePoll
